import { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { advanceDealStage, denyDeal } from '../../services/dealService';
import { sendCompanyNotification, sendInternalNotification } from '../../services/pifService';
import { useAuth } from '../../hooks/useAuth';
import Button from '../shared/UI/Button';
import SelectField from '../shared/Forms/SelectField';
import FormField from '../shared/Forms/FormField';

export default function HOPReviewForm({ deal, approvalSequence, onSaved }) {
  const { user, userProfile } = useAuth();
  const [form, setForm] = useState({
    notes: '',
    outcome: '',
    send_back_reason: '',
    decline_reason: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.notes?.trim()) e.notes = 'Review notes are required';
    if (!form.outcome) e.outcome = 'Please select an outcome';
    if (form.outcome === 'send_back' && !form.send_back_reason?.trim()) {
      e.send_back_reason = 'Please provide a reason for sending back';
    }
    if (form.outcome === 'decline' && !form.decline_reason?.trim()) {
      e.decline_reason = 'Please provide a reason for declining';
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, 'deals', deal.id, 'analyst_reviews'), {
        deal_id: deal.id,
        review_type: 'hop_review',
        reviewer_id: user?.uid,
        reviewer_name: userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown',
        performed_by_name: userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown',
        pif_submission_id: null,
        notes: form.notes,
        sector_concentration_flag: false,
        obligor_exposure_flag: false,
        dry_powder_flag: false,
        outcome: form.outcome,
        send_back_reason: form.send_back_reason || null,
        completed_at: serverTimestamp(),
        created_at: serverTimestamp(),
      });

      if (form.outcome === 'advance_to_iic') {
        // Get the first stage in the approval sequence
        const firstStage = (approvalSequence?.stages || []).find(s => s.stage_order === 1);
        await advanceDealStage({
          dealId: deal.id,
          newStage: 'iic_approval',
          performedBy: user?.uid,
          performedByName: userProfile?.full_name || userProfile?.display_name || null,
          action: 'hop_review_completed',
        });

        // Create first IIC approval document
        await addDoc(collection(db, 'deals', deal.id, 'iic_approvals'), {
          deal_id: deal.id,
          gate: 'pre_screening',
          stage_order: 1,
          approver_role: firstStage?.role || 'head_of_pipeline',
          approver_id: null,
          status: 'pending',
          notes: null,
          requested_at: serverTimestamp(),
          decided_at: null,
        });

        // Notify the first IIC approver (HoP themselves)
        await sendInternalNotification({
          recipientIds: [user?.uid],
          subject: `IIC Approval Required — ${deal.company_name}`,
          body: `Your IIC approval is required for ${deal.company_name}. Please log in to review and approve.`,
          dealId: deal.id,
        }).catch(() => {});

      } else if (form.outcome === 'send_back') {
        await advanceDealStage({
          dealId: deal.id,
          newStage: 'analyst_review',
          performedBy: user?.uid,
          performedByName: userProfile?.full_name || userProfile?.display_name || null,
          action: 'deal_sent_back',
        });
        // Notify analyst
        if (deal.assigned_analyst_id) {
          await sendInternalNotification({
            recipientIds: [deal.assigned_analyst_id],
            subject: `Deal Returned — ${deal.company_name}`,
            body: `The Head of Pipeline has returned ${deal.company_name} for further work.\n\nReason: ${form.send_back_reason}`,
            dealId: deal.id,
          }).catch(() => {});
        }
      } else if (form.outcome === 'decline') {
        await denyDeal({
          dealId: deal.id,
          reason: form.decline_reason,
          performedBy: user?.uid,
          performedByName: userProfile?.full_name || userProfile?.display_name || null,
          stageAtTime: 'hop_review',
        });
        await sendCompanyNotification({
          dealId: deal.id,
          notificationType: 'not_advancing',
          additionalData: {},
        }).catch(() => {});
      }

      setSuccess(true);
      setTimeout(() => onSaved?.(), 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Review submitted!</div>}

      <div className="card mb-4">
        <div className="card-header"><h2>Head of Pipeline Review</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <FormField label="Review Notes" required error={errors.notes} className="form-full">
              <textarea className={`form-textarea ${errors.notes ? 'error' : ''}`}
                value={form.notes} onChange={e => set('notes', e.target.value)} rows={5} />
            </FormField>

            <SelectField
              label="Outcome"
              required
              error={errors.outcome}
              value={form.outcome}
              onChange={e => set('outcome', e.target.value)}
              options={[
                { value: 'advance_to_iic', label: 'Advance to IIC Approval' },
                { value: 'send_back', label: 'Send Back to Analyst' },
                { value: 'decline', label: 'Decline — Do Not Advance' },
              ]}
            />

            {form.outcome === 'send_back' && (
              <FormField label="Send Back Reason" required error={errors.send_back_reason} className="form-full">
                <textarea className={`form-textarea ${errors.send_back_reason ? 'error' : ''}`}
                  value={form.send_back_reason} onChange={e => set('send_back_reason', e.target.value)} rows={3} />
              </FormField>
            )}
            {form.outcome === 'decline' && (
              <FormField label="Decline Reason" required error={errors.decline_reason} className="form-full">
                <textarea className={`form-textarea ${errors.decline_reason ? 'error' : ''}`}
                  value={form.decline_reason} onChange={e => set('decline_reason', e.target.value)} rows={3} />
              </FormField>
            )}
          </div>
        </div>
      </div>

      <div className="flex-between">
        <div />
        <Button variant="primary" type="submit" loading={saving}>Submit Review</Button>
      </div>
    </form>
  );
}
